import { IChildQuestion, IQuestionDisplayType, IQuestionTypeName } from "../types";
import type { SelectionMap } from "./child_config_controls";
import { AdvancedSelect } from '../../tv_utils/advanced_select';
import { QuestionModal } from "./question_modal";

/**
 * Interface for data needed to initialize a ChildQuestionInput.
 * 
 * @property {IChildQuestion} data Child question data from ConfiguratorV2 API.
 * @property {HTMLElement} template HTML template for the child question.
 */
interface IChildQuestionInputData {
    data: IChildQuestion;
    template: HTMLElement;
}

/**
 * Class for handling the input for a single child question. Each config question input is rendered by the previous
 * config question input (except for the root/default question, which is rendered by ChildQuestionControls).
 * 
 * @property {Map<number, IChildQuestionInputData} all_child_questions Map containing both the data and the HTML
 *     template for every child question.
 * @property {number} question_id The id for the question corresponding to the child question.
 * @property {number} parent_child_relation_id The id for the parent child relation.
 * @property {number} child_question_id The id for the child question.
 * @property {IChildQuestion} child_question_data The data for this child question.
 * @property {ChildQuestionInput} next_question The object handling the next child question input.
 * @property {HTMLElement} control_element The container element for the child question input.
 * @property {(HTMLInputElement | HTMLSelectElement)[]} input_elements All the input elements for this child question.
 * @property {HTMLCollectionOf<HTMLInputElement>} custom_size_inputs Input elements for custom size questions.
 * @property {number} selected_option The option id of the selected option.
 * @property {SelectionMap} initial_selections The initial selections used to set the initial values for the inputs.
 * @property {Function} option_selected_callback The callback run after an option is selected.
 * @property {IQuestionTypeName} control_type The type of control ('Dropdown', 'Radio', etc.).
 */
export class ChildQuestionInput {
    all_child_questions: Map<number, IChildQuestionInputData>;
    question_id: number;
    parent_child_relation_id: number;
    child_question_id: number;
    child_question_data: IChildQuestion;
    next_question: ChildQuestionInput = null;
    control_element: HTMLElement;
    control_element_template: HTMLElement;
    input_elements: (HTMLInputElement | HTMLSelectElement | HTMLLIElement)[];
    custom_size_inputs: HTMLCollectionOf<HTMLInputElement>;
    selected_option: number = 0;
    initial_selections: SelectionMap = null;
    option_selected_callback: Function;
    control_type: IQuestionTypeName;
    control_display_type: IQuestionDisplayType;
    advanced_select: AdvancedSelect;
    event_listeners: Map <HTMLElement, EventListener> = new Map();
    update_quantity: Function;
    custom_size_unit: number = 1;
    ignore_multiple_inputs: boolean;
    question_modals: Map<number, QuestionModal>;

    /**
     * Instantiates a ChildQuestionInput.
     *
     * @param {Map<number, IChildQuestionInputData} all_child_questions Template and child question data for all child
     *     question inputs.
     * @param {number} child_question_id The id of the child question.
     * @param {number} parent_child_relation_id The id of the parent child relation.
     * @param {Function} option_selected_callback A callback to be run whenever an option is selected.
     * @param {SelectionMap} selections Initial selections used to set initial values.
     */
    constructor(
        all_child_questions: Map<number, IChildQuestionInputData>,
        child_question_id: number,
        parent_child_relation_id: number,
        option_selected_callback: Function,
        update_quantity: Function,
        selections: SelectionMap = null,
        question_modals: Map<number, QuestionModal> = null
    ) {
        this.all_child_questions = all_child_questions;
        this.child_question_id = child_question_id;
        const child_question = this.all_child_questions.get(child_question_id);
        this.child_question_data = child_question.data;
        this.question_id = child_question.data.questionId;
        this.option_selected_callback = option_selected_callback;
        this.update_quantity = update_quantity;
        this.initial_selections = selections;
        this.control_type = child_question.data.question.type.name;
        this.control_display_type = child_question.data.question.type.displayType;
        this.parent_child_relation_id = parent_child_relation_id;
        this.question_modals = question_modals;
        // Treat multiple inputs as single inputs for product previews with photo upload.
        const product_preview = document.getElementById('product_preview') as HTMLInputElement;
        this.ignore_multiple_inputs = product_preview && 
            (product_preview.value === 'photo_upload' || product_preview.value === 'photo_upload_bg');

        // Render the question input element.
        const container = document.getElementById(`js-config-questions-${parent_child_relation_id}`);
        this.control_element_template = child_question.template;
        this.control_element = this.control_element_template.cloneNode(true) as HTMLElement;
        container.appendChild(this.control_element);

        this.bind();

        // Use the passed selections or default option to set the initial input value.
        if (selections && selections.has(this.question_id)) {
            const selected_option = selections.get(this.question_id);
            this.select_option(selected_option, true);
        } else {
            const default_option_id = this.get_default_option();
            this.select_option(default_option_id, true);
        }

        if (this.control_display_type === 'Modal') {
            this.initialize_configuration_modal_picker();
        } else if (this.control_type === 'Custom Size Input') {
            for (const question_data of this.all_child_questions.values()) {
                const question = question_data.data.question;
                if (question.type.name === 'Custom Size') {
                    for (const option of question.questionOptions) {
                        if (option.customSize) {
                            const custom_size_data = JSON.parse(option.companyDetails[0].questionOptionValue);
                            if ('unit_value' in custom_size_data) {
                                this.custom_size_unit = Number.parseFloat(custom_size_data.unit_value);
                                break;
                            }
                        }
                    }
                    break;
                }
            }

            this.custom_size_inputs = container.getElementsByClassName(
                'js-custom-size-input') as HTMLCollectionOf<HTMLInputElement>;
            // Confirm that current input values are valid for selected SKU.
            // A discrepancy can occur when loading a non-default sku via url.
            const projected_selection = this.get_custom_size_selection();
            let corrected_value = 0;
            // If input values are invalid, set both inputs to the sq root of the selected sq ft.
            if (this.selected_option != parseInt(projected_selection.value)) {
                const selected_sqft = (this.input_elements[0] as HTMLSelectElement).selectedOptions[0].innerHTML;
                corrected_value = Math.floor(Math.sqrt(parseFloat(selected_sqft)) * 12 / this.custom_size_unit);
            }

            for (const custom_size_input of this.custom_size_inputs) {
                custom_size_input.addEventListener('change',
                    this.custom_size_input_changed.bind(this, custom_size_input)
                );
                // Prevent implicit submission with Enter key.
                custom_size_input.addEventListener('keypress', (event) => {
                    if (event.key === "Enter") {
                        event.preventDefault();
                        this.custom_size_input_changed(custom_size_input)
                    }
                });
                if (corrected_value) {
                    custom_size_input.value = corrected_value.toFixed(
                        parseInt(custom_size_input.dataset.decimalAmount)
                    );
                }
            }
        }
        this.quantity_changed(undefined, true);
    }

    /**
     * Establishes DOM references and attaches event listeners.
     * 
     * @returns {void}
     */
    bind(): void {
        this.input_elements = Array.from(this.control_element.querySelectorAll(
            `.js-configurator-control-${this.parent_child_relation_id}`
        ));

        for (const input of this.input_elements) {
            // Advanced select questions continue because change event doesn't trigger.
            if (this.control_display_type === 'Modal') {
                continue;
            }

            input.addEventListener('change',
                (this.control_display_type === 'Input' || this.control_type == 'Quantity-Option1')
                    ? this.input_control_changed.bind(this)
                    : this.control_changed.bind(this)
            );
        }

        // Step number buttons.
        const step_number_buttons = this.control_element.getElementsByClassName('js-question-step-number-input');
        for (const step_number_button of step_number_buttons) {
            step_number_button.addEventListener('click', (event) => {
                this.update_number_input(event)
            });
        }

        // Quantity controls
        const quantity_text_elements = this.control_element.getElementsByClassName('js-quantity-text');
        for (const quantity_text of quantity_text_elements) {
            this.add_quantity_input_text_links(quantity_text);

            const quantity_text_links = quantity_text.getElementsByClassName('js-quantity-text-link');
            for (const quantity_text_link of quantity_text_links) {
                quantity_text_link.addEventListener('click', this.get_recommended_quantities.bind(this));
            }
        }

        if (quantity_text_elements && quantity_text_elements.length) {
            const quantity_containers = document.getElementsByName('configurator-quantity-container');
            for (const quantity_container of quantity_containers) {
                quantity_container.classList.add('tv-display__none');
            }
        }

        const quantity_inputs = this.control_element.getElementsByClassName('js-text-quantity-input');
        let total = 0;
        for (const quantity_input of quantity_inputs) {
            quantity_input.addEventListener('change', this.adjust_quantity_input.bind(this));
            total += Number.parseInt((quantity_input as HTMLInputElement).value||'0', 10);
        }
        if (quantity_inputs.length && !total) {
            const quantity_input = document.getElementById('js-config-quantity') as HTMLInputElement;
            this.get_recommended_quantities(Number.parseInt(quantity_input.value || '0', 10));
        }
    }

    /**
     * Event handler for non-input control changes
     * 
     * @param {Event} e the event
     * @returns {void}
     */
     control_changed(e: Event): void {
        const option_id = parseInt((<HTMLInputElement>e.target).value);
        this.option_selected(option_id, true);
    }
    /**
     * Event handler for input control changes
     * 
     * @param {Event} e the event
     * @returns {void}
     */
     input_control_changed(e: Event): void {
        const option_id = parseInt((<HTMLInputElement>e.target).dataset.optionId);
        this.option_selected(option_id, true);
    }

    /**
     * Gets the default option for the child question.
     *
     * @returns {number} The option id of the default option.
     */
    get_default_option(): number {
        // Check if there's a default question option relation first.
        const default_question_option_relation = this.child_question_data.questionOptionRelations.find(
            qor => qor.default
        );
        if (default_question_option_relation) {
            return default_question_option_relation.questionOptionId;
        }

        // Then check if there's a default option.
        const default_option = this.child_question_data.question.questionOptions.find(qo => qo.default);
        if (default_option) {
            return default_option.id;
        }

        // Otherwise, just use the first option as the default.
        const first_option = this.child_question_data.question.questionOptions[0];
        return first_option.id;
    }

    /**
     * Selects the specified option. Used when we need to manually change the current selection without user input.
     *
     * @param {number} option_id The option to select.
     * @param {boolean} question_added Whether this is initializing a new question input.
     * @returns {void}
     */
    select_option(option_id: number, question_added: boolean = false, use_callback: boolean = false): void {
        switch (this.control_type) {
            case 'Dropdown':
            case 'Custom Size Input':
                this.input_elements[0].value = option_id.toString();
                break;
            case 'Radio': // Fall through
            case 'Radio (Text Only)':
            case 'Color Swatches':
            case 'Custom Size':
            case 'Toggle':
                const radio = <HTMLInputElement>this.input_elements.find(radio => radio.value === option_id.toString());
                radio.checked = true;
                break;
            case 'Input': // Fall through
            case 'Multiple Input':
            case 'Compound Multiple Input':
            case 'Quantity-Option1':
                break;
            case 'Color Picker':
            case 'Wall Picker':
                // Create advanced select dropdown.
                const advanced_select_dropdown = this.control_element.querySelector('.js-advanced-select');
                this.advanced_select = new AdvancedSelect(advanced_select_dropdown);

                const swatch = this.input_elements.find(swatch => swatch.value === option_id);
                const select_event = new Event("click", {"bubbles":false});
                swatch.dispatchEvent(select_event);
                
                // Set advanced select callback after initial option is set.
                this.advanced_select.set_callback((selected_option) => {
                    const option_id = parseInt((selected_option as HTMLInputElement).dataset.option_id);
                    this.option_selected(option_id, true);
                });

                break;
        }

        this.option_selected(option_id, use_callback, question_added);
    }

    /**
     * Update the styling for radio inputs based on the current selection.
     * 
     * @returns {void}
     */
    update_selected_radio_input_styles(control_class: string): void {
        const radio_cards = this.control_element.querySelectorAll(`.${control_class}`);
        for (const radio_card of radio_cards) {
            const radio_input = radio_card.querySelector<HTMLInputElement>('input[type="radio"]');
            const radio_ok_icon = radio_card.querySelector('.tv-radio-ok-icon');

            if (radio_input.checked) {
                radio_card.classList.add(`${control_class}-selected`);
                if (radio_ok_icon) {
                    radio_ok_icon.classList.remove('tv-display__none');
                }
            } else {
                radio_card.classList.remove(`${control_class}-selected`);
                if (radio_ok_icon) {
                    radio_ok_icon.classList.add('tv-display__none');
                }
            }
        }
    
        // Handle displaying of no-wall sections based on selected option.
        const one_wall = document.querySelector('[value="1WL"]');
        const two_wall = document.querySelector('[value="2WL"]');

        if (one_wall || two_wall) {    
            const no_wall_identifiers = document.querySelectorAll('[value="NW"]');
            const wall_one_selected = one_wall.parentElement.classList.contains(`${control_class}-selected`);
            const wall_two_selected = two_wall.parentElement.classList.contains(`${control_class}-selected`);

            if (wall_one_selected || wall_two_selected) {
                for (const no_wall_identifier of no_wall_identifiers) {
                    no_wall_identifier.parentElement.parentElement.parentElement.classList.add('tv-display__none');
                }
            } else {
                for (const no_wall_identifier of no_wall_identifiers) {
                    no_wall_identifier.parentElement.parentElement.parentElement.classList.remove('tv-display__none');
                }
            }
        }
    }

    /**
     * Recursively deletes all next questions.
     * 
     * @returns {void}
     */
    delete_all_next_questions(): void {
        if (this.next_question) {
            this.next_question.delete_all_next_questions();
            // Store text from Input questions in control template to preserve user's input.
            if (this.next_question.control_type === 'Input') {
                const input_template = this.next_question.control_element_template.querySelector(
                    `.js-configurator-control-${this.next_question.parent_child_relation_id}`
                ) as HTMLTextAreaElement;
                input_template.value = this.next_question.input_elements[0].value.toString();
            } else if (this.next_question.control_type === 'Custom Size Input') {
                const input_templates = this.next_question.control_element_template.getElementsByClassName(
                    'js-custom-size-input') as HTMLCollectionOf<HTMLInputElement>;
                for (let i = 0; i < input_templates.length; i++) {
                    input_templates[i].value = this.next_question.custom_size_inputs[i].value;
                }
            } else if (this.next_question.control_type === 'Multiple Input' || this.next_question.control_type === 'Compound Multiple Input') {
                const input_template = this.next_question.control_element_template.querySelector(
                    '.js-multiple-input-data'
                ) as HTMLInputElement;
                input_template.value = JSON.stringify(this.next_question.input_elements.map( e => e.value.toString() ));
            } else if (this.next_question.control_type === 'Quantity-Option1') {
                const input_template = this.next_question.control_element_template.querySelector(
                    '.js-quantity-input-data'
                ) as HTMLInputElement;
                const input_data =Object.fromEntries(
                    this.next_question.input_elements.map( e => [ e.dataset.optionName, e.value.toString() ] )
                );
                input_template.value = JSON.stringify(input_data);

                const template_input_elements = this.next_question.control_element_template.querySelectorAll('.js-text-quantity-input') as NodeListOf<HTMLInputElement>
                for (const input of template_input_elements) {
                    input.value = input_data[input.dataset.optionName];
                }
            }
            this.next_question.control_element.remove();
            // Remove event listeners related to configuration modal.
            this.next_question.remove_event_listeners();
        }

        this.next_question = null;
    }

    /**
     * Removes event listeners and disconnects observers.
     * 
     * @returns {void}
     */
    remove_event_listeners(): void {
        if (this.advanced_select) {
            this.advanced_select.remove_listeners();
            this.event_listeners.forEach((listener, element) => {
                element.removeEventListener('click', listener);
            });
        }
    }

    /**
     * Gets the current selections for this child question inputs and all following inputs.
     *
     * @returns {SelectionMap} A map of the current selections (formatted as <question_id, option_id>).
     */
    get_question_branch_selections(): SelectionMap {
        const selections = new Map();
        let child_question: ChildQuestionInput = this;
        while (child_question) {
            selections.set(child_question.question_id, child_question.selected_option);
            child_question = child_question.next_question;
        }

        return selections;
    }

    /**
     * All the logic that needs to run whenever an option is selected (whether it's initiated by the user or by the
     * select_option method), including the option_selected_callback if applicable.
     *
     * @param {number} option_id The id of the selected option.
     * @param {boolean} use_callback Whether to run this.option_selected_callback.
     * @param {boolean} question_added Whether this is initializing a new question input.
     * @returns {void}
     */
    option_selected(option_id: number, use_callback: boolean = false, question_added: boolean = false): void {
        if (this.selected_option === option_id) {
            if (use_callback) {
                this.option_selected_callback(this, false, use_callback, false);
            }
            return;
        }

        this.selected_option = option_id;

        if (this.control_type === 'Radio' || this.control_type === 'Color Swatches') {
            let control_class = 'tv-radio-card';
            if (this.control_type === 'Color Swatches') {
                control_class = 'tv-color-swatches__radio';
                // Add selected color name next to question label.
                const selected_swatch_string = 'question_' + this.child_question_id + '_' + this.parent_child_relation_id + '_option_' + this.selected_option;
                const color_name_labels = document.getElementsByClassName('js-selected-color-swatch-name');

                for (const color_name_label of color_name_labels) {
                    if (color_name_label.parentElement.nextElementSibling.contains(
                            document.getElementById(selected_swatch_string))) {
                        color_name_label.textContent = (this.control_element.querySelector(
                            `.js-color-name-${option_id}`) as HTMLInputElement).value;
                    } else {
                        color_name_label.textContent = '';
                    }
                }
            }


            this.update_selected_radio_input_styles(control_class);
        }

        // Get the next child question from the question option relation.
        const question_option_relation = this.child_question_data.questionOptionRelations.find(
            qor => qor.questionOptionId === option_id
        );
        const next_child_question = this.all_child_questions.get(question_option_relation.nextChildQuestionId);
        
        // If there's already a next question, then we need to delete and rerender all the next questions
        // (using the current selections if possible).
        let selections = this.initial_selections;
        this.initial_selections = null;
        if (this.next_question) {
            selections = this.get_question_branch_selections();
            this.delete_all_next_questions();
        }

        // Update the next child question.
        if (next_child_question) {
            this.next_question = new ChildQuestionInput(
                this.all_child_questions,
                next_child_question.data.id,
                this.parent_child_relation_id,
                this.option_selected_callback,
                this.update_quantity,
                selections,
                this.question_modals
            );
        }

        if (use_callback || question_added) {
            this.option_selected_callback(this, question_added);
        }
    }

    /**
     * Handles quantity changed events
     * 
     * @param {number} quantity
     * @param {boolean} initialize if this is called from constructor
     * @returns {void}
     */
    quantity_changed(quantity?: number, initialize: boolean = false): void {
        switch(this.control_type) {
            case 'Quantity-Option1':
                if (typeof quantity === 'undefined') {
                    const quantity_input = <HTMLInputElement>document.getElementById('js-config-quantity');
                    quantity = quantity_input ? parseInt(quantity_input.value) : 3;
                }

                const quantity_inputs = this.control_element.getElementsByClassName('js-text-quantity-input');
                let total = 0;
                for (const element of quantity_inputs) {
                    const quantity_input = element as HTMLInputElement;
                    total += Number.parseInt(quantity_input.value || '0', 10);
                }
    
                if (quantity !== total) {
                    this.get_recommended_quantities(quantity);
                }
                break;
            case 'Multiple Input':
            case 'Compound Multiple Input':
                if (typeof quantity === 'undefined') {
                    const quantity_input = <HTMLInputElement>document.getElementById('js-config-quantity');
                    quantity = quantity_input ? parseInt(quantity_input.value) : 3;
                }
                this.update_multiple_input_controls(quantity, initialize, this.control_type === 'Compound Multiple Input');
                break;
        }

        if (!initialize && this.next_question) {
            this.next_question.quantity_changed(quantity);
        }
    }

    /**
     * Handles creating and deleting text boxes for Multiple Inputs
     * 
     * @param {number} quantity
     * @param {boolean} initialize if this is called from constructor
     * @returns {void}
     */
    update_multiple_input_controls(quantity: number, initialize: boolean, compound: boolean): void {
        const controls = Array.from(this.control_element.getElementsByClassName('js-multiple-input-control'));
        const values = JSON.parse((this.control_element_template.getElementsByClassName('js-multiple-input-data')[0] as HTMLInputElement).value || '[]');
        let default_value = (this.control_element_template.getElementsByClassName('js-multiple-input-default')[0] as HTMLInputElement).value;
        if (compound) {
            default_value = JSON.parse(default_value);
        }

        // Prevent creation of extra inputs by setting quantity to 1.
        if (this.ignore_multiple_inputs) {
            quantity = 1;
        }

        while (controls.length > quantity) {
            controls.pop().remove();
        }
        if (initialize) {
            for (const index in controls) {
                if (compound) {
                    const input = controls[index].getElementsByTagName('textarea')[0] as HTMLTextAreaElement;
                    input.value = values[index] || default_value[0];
                    const compound_input = controls[index].getElementsByTagName('textarea')[1] as HTMLTextAreaElement;
                    compound_input.value = values[parseInt(index) + 1] || default_value[1];
                }
                else {
                    const input = controls[index].getElementsByTagName('textarea')[0] as HTMLTextAreaElement;
                    input.value = values[index] || default_value;        
                }   
            }
        }
        let compound_offset = controls.length;
        while (controls.length < quantity) {
            const new_control = controls[0].cloneNode(true) as HTMLElement;
            const new_label = new_control.getElementsByTagName('label')[0] as HTMLLabelElement;
            const new_input = new_control.getElementsByTagName('textarea')[0] as HTMLTextAreaElement;
            new_input.addEventListener('change', this.input_control_changed.bind(this));
            new_label.textContent = new_label.textContent.trim() + ' ' + (controls.length + 1);

            if (compound) {
                new_input.name = `configuration[child_configurations][${this.parent_child_relation_id}][questions][${this.child_question_data.questionId}][compound_multiple_input][${controls.length}][0]`;
                const compound_input = new_control.getElementsByTagName('textarea')[1] as HTMLTextAreaElement;
                compound_input.name = `configuration[child_configurations][${this.parent_child_relation_id}][questions][${this.child_question_data.questionId}][compound_multiple_input][${controls.length}][1]`;
                new_input.value = values[controls.length + compound_offset++] || default_value[0];
                compound_input.value = values[controls.length + compound_offset] || default_value[1];
            }
            else {
                new_input.name = `configuration[child_configurations][${this.parent_child_relation_id}][questions][${this.child_question_data.questionId}][multiple_input][${controls.length}]`;
                new_input.value = values[controls.length] || default_value;
            } 

            controls[controls.length - 1].after(new_control);
            controls.push(new_control);
        }

        this.input_elements = Array.from(this.control_element.querySelectorAll(
            `.js-configurator-control-${this.parent_child_relation_id}`
        ));
    }


    /**
     * Handles changes to Custom Size Inputs and selects the matching sqft size option.
     *
     * @param {HTMLInputElement} custom_size_input The input changed.
     * @returns {void}
     */
    custom_size_input_changed(custom_size_input : HTMLInputElement): void {
        // Clamp input values.
        this.number_input_changed(custom_size_input, parseFloat(custom_size_input.value));
        const selected_size = this.get_custom_size_selection();
        this.select_option(parseInt(selected_size.value), false, true);
    }

    /**
     * Calculates the sqft based on the two custom size inputs and returns the corresponding option.
     *
     * @returns {HTMLOptionElement} The options which corresponds to the current sq ft selected.
     */
    get_custom_size_selection(): HTMLOptionElement {
        // Convert what was entered (inches) into feet.
        const input_1_feet = (parseFloat(this.custom_size_inputs[0].value) * this.custom_size_unit / 12);
        const input_2_feet = (parseFloat(this.custom_size_inputs[1].value) * this.custom_size_unit / 12);

        // Calculate sqft of inputs.
        const current_sqft = input_1_feet * input_2_feet;
        // Get the possible values.
        const sqft_size_control = this.input_elements[0] as HTMLSelectElement;
        let selected_size = sqft_size_control.options[0];
        // Options must be in order from least to greatest.
        for (const option of sqft_size_control.options) {
            const sqft_option = parseFloat(option.innerHTML);
            selected_size = option;
            if (sqft_option >= current_sqft) {
                break;
            }
        }

        return selected_size;
    }

    /**
     * Update the number input from the step button.
     *
     * @param {Event} event The event from the click.
     * @returns {void}
     */
    update_number_input(event : Event) : void {
        const step_data = (event.target as HTMLElement).dataset;
        const step_type = step_data.stepType;
        const step_value = parseFloat(step_data.stepValue);
        const input_id = step_data.inputId;
        const number_input = <HTMLInputElement>document.getElementById(input_id);

        // Determine if the adjustment value should be positive or negative based on the step type.
        const adjustment_value = (step_type === 'add') ? Math.abs(step_value) : -Math.abs(step_value);

        // Calculate the updated value with the current input value and the adjustment value.
        const updated_value = parseFloat(number_input.value) + adjustment_value;

        // Custom Size Inputs handle validation separately.
        if (this.control_type == 'Custom Size Input') {
            number_input.value = updated_value.toString();
            this.custom_size_input_changed(number_input);
        }
        else {
            this.number_input_changed(number_input, updated_value);
        }
    }

    /**
     * Clamps the updated value of a number input and displays the correct number of digits.
     *
     * @param {HTMLInputElement} custom_size_input The input changed.
     * @param {number} updated_value The new value.
     * @returns {void}
     */
    number_input_changed(number_input : HTMLInputElement, updated_value : number) : void {
        const min_value = parseFloat(number_input.getAttribute('min'));
        const max_value = parseFloat(number_input.getAttribute('max'));
        const decimal_amount = parseInt(number_input.dataset.decimalAmount);

        // Clamp value min/max.
        updated_value = Math.min(Math.max(updated_value, min_value), max_value);
        number_input.value = updated_value.toFixed(decimal_amount);
    }

    /**
     * Initialize and attach event listeners to configuration modal specific elements (Color Picker, Wall Picker).
     *
     * @returns {void}
     */
    initialize_configuration_modal_picker(): void {
        const question_modal = this.question_modals.get(this.question_id);
        const modal_openers = this.control_element.getElementsByClassName(
            `js-question-modal-opener-${this.question_id}`) as HTMLCollectionOf<HTMLElement>;
        for(const modal_opener of modal_openers) {
            // Functionality for modal openers.
            const open_button_listener = () => {
                question_modal.modal_opened(this)
            };
            this.event_listeners.set(modal_opener, open_button_listener);
            modal_opener.addEventListener('click', open_button_listener);
        }
        if (this.control_type === 'Wall Picker') {
            for (const config_modal_swatch of question_modal.config_modal_swatches) {
                // Functionality for remove wall button.
                const swatch_label = config_modal_swatch.querySelector('.tv-color-picker__label');
                if (swatch_label.innerHTML == 'No Wall') {
                    const js_remove_wall_button = this.control_element.querySelector('.js-remove-wall') as HTMLElement;
                    const remove_button_listener = () => {
                        config_modal_swatch.click();
                        question_modal.remove_wall(this)
                    };
                    this.event_listeners.set(js_remove_wall_button, remove_button_listener);
                    js_remove_wall_button.addEventListener('click', remove_button_listener);
                }
                // Set initial swatch selection.
                if (config_modal_swatch.value === this.selected_option) {
                    question_modal.set_containers(this);
                    question_modal.select_configuration_modal_swatch(config_modal_swatch);
                    question_modal.toggle_wall_picker_controls(config_modal_swatch);
                }
            }
        }
    }

    add_quantity_input_text_links(element: Element) {
        let node = element.firstChild;
        while(node) {
            if (node.nodeType === Node.ELEMENT_NODE) {
                this.add_quantity_input_text_links(node as Element);
            } else if (node.nodeType === Node.TEXT_NODE) {
                const regex = /(.*)\[([^]])\]/g;
                let text = node.textContent;
                let match;
                while(match = /\[[^\[\]]+\]/.exec(text)) {
                    node = (node as Text).splitText(match.index);
                    const link = document.createElement('a') as Element;
                    link.classList.add('js-quantity-text-link')
                    link.append(document.createTextNode(match[0].substring(1, match[0].length - 1)));
                    element.insertBefore(link, node);
                    text = text.substring(match.index + match[0].length);
                }
                node.textContent = text;

                const pos = text.indexOf('[');
                if (pos > -1) {
                    // Handle link with elements inside
                    let end = node.nextSibling;
                    while(end && (end.nodeType !== Node.TEXT_NODE || !(match = /^[^\[\]]+\]/.exec(end.textContent)))) {
                        end = end.nextSibling;
                    }

                    if (end) {
                        node.textContent = text.substring(0, pos);
                        node = node.nextSibling;
                        end.textContent = end.textContent.substring(match[0].length);
                        const link = document.createElement('a') as Element;
                        link.classList.add('js-quantity-text-link')
                        link.append(document.createTextNode(text.substring(pos+1)));
                        while(node && node !== end) {
                            const next = node.nextSibling;
                            link.append(node);
                            node = next;
                        }
                        link.append(document.createTextNode(match[0].substring(0, match[0].length - 1)));
                        element.insertBefore(link, node);
                    }
                }
            }
            node = node.nextSibling;
        }
    }

    get_recommended_quantities(quantity?: number) {
        const quantity_inputs = this.control_element.getElementsByClassName('js-text-quantity-input');
        if (typeof quantity === 'undefined') {
            quantity = 0;
            for (const element of quantity_inputs) {
                const quantity_input = element as HTMLInputElement;
                quantity += Number.parseInt(quantity_input.value || '0', 10);
            }
        }

        let total = 0;
        let default_quantity_input;
        for (const element of quantity_inputs) {
            const quantity_input = element as HTMLInputElement;
            if (quantity_input.dataset.default) {
                default_quantity_input = quantity_input;
            } else {
                const allocation = Number.parseInt(quantity_input.dataset.allocation || '0', 10)/100.0;
                let value = Math.floor(allocation * quantity);
                quantity_input.value = value.toString();
                total += value;
            }
        }

        if (default_quantity_input) {
            default_quantity_input.value = Math.max(quantity - total, 0).toString();
        }
    }

    adjust_quantity_input() {
        let total = 0;
        const quantity_inputs = this.control_element.getElementsByClassName('js-text-quantity-input');
        for (const quantity_input of quantity_inputs) {
            const value = Number.parseInt((quantity_input as HTMLInputElement).value, 10);
            if (Number.isNaN(value)) {
                (quantity_input as HTMLInputElement).value = '0';
            } else {
                total += value;
            }
        }

        const quantity_input = document.getElementById('js-config-quantity') as HTMLInputElement;
        quantity_input.value = total.toString();
        this.update_quantity();
    }
}
